/**
 * ProjectDetailsMobileHeader Component
 *
 * Compact mobile header for project details following industry patterns:
 * - Minimal header: Back + Title + Overflow menu
 * - Actions (Status, Settings, Owner) moved to overflow menu
 * - ~60px height to maximize content space
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown, Menu, Select, Divider } from 'antd';
import { space } from '../../configs/designTokens';
import {
  LeftOutlined,
  MoreOutlined,
  SettingOutlined,
  UserOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import '../../styles/index.scss';

const { Option } = Select;

const STATUS_OPTIONS = [
  { value: 'NOT_STARTED', label: 'Not Started' },
  { value: 'PLANNING', label: 'Planning' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
];

const ProjectDetailsMobileHeader = ({
  project,
  onBack,
  onStatusUpdate,
  onSettingsClick,
  updateLoading,
}) => {
  const [menuVisible, setMenuVisible] = useState(false);

  const handleStatusSelect = (status) => {
    onStatusUpdate(status);
    setMenuVisible(false);
  };

  const menu = (
    <Menu className="project-mobile-header__menu">
      {/* Owner Info */}
      {project?.owner && (
        <>
          <Menu.Item key="owner" disabled className="project-mobile-header__menu-owner">
            <UserOutlined className="project-mobile-header__menu-icon" />
            <span>Owner: {startCase(project.owner.name || project.owner.username)}</span>
          </Menu.Item>
          <Divider style={{ margin: `${space[2]}px 0` }} />
        </>
      )}

      {/* Status Options */}
      <Menu.ItemGroup title="Update Status">
        {STATUS_OPTIONS.map((option) => (
          <Menu.Item
            key={option.value}
            onClick={() => handleStatusSelect(option.value)}
            disabled={updateLoading}
            className="project-mobile-header__menu-status"
          >
            <span>{option.label}</span>
            {project?.status === option.value && (
              <CheckOutlined className="project-mobile-header__menu-check" />
            )}
          </Menu.Item>
        ))}
      </Menu.ItemGroup>

      <Divider style={{ margin: `${space[2]}px 0` }} />

      {/* Settings */}
      <Menu.Item
        key="settings"
        onClick={() => {
          setMenuVisible(false);
          onSettingsClick();
        }}
        icon={<SettingOutlined />}
      >
        Project Settings
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="project-mobile-header">
      {/* Back Button */}
      <Button
        type="text"
        className="project-mobile-header__back"
        onClick={onBack}
        icon={<LeftOutlined />}
      />

      {/* Title */}
      <h1 className="project-mobile-header__title">
        {project?.name || 'Project'}
      </h1>

      {/* Overflow Menu */}
      <Dropdown
        overlay={menu}
        trigger={['click']}
        placement="bottomRight"
        visible={menuVisible}
        onVisibleChange={setMenuVisible}
      >
        <Button
          type="text"
          className="project-mobile-header__menu-btn"
          icon={<MoreOutlined />}
        />
      </Dropdown>
    </div>
  );
};

ProjectDetailsMobileHeader.propTypes = {
  project: PropTypes.shape({
    name: PropTypes.string,
    status: PropTypes.string,
    owner: PropTypes.shape({
      name: PropTypes.string,
      username: PropTypes.string,
    }),
  }),
  onBack: PropTypes.func.isRequired,
  onStatusUpdate: PropTypes.func.isRequired,
  onSettingsClick: PropTypes.func.isRequired,
  updateLoading: PropTypes.bool,
};

ProjectDetailsMobileHeader.defaultProps = {
  project: null,
  updateLoading: false,
};

export default ProjectDetailsMobileHeader;
